import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Video, Phone, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/* Practice Setup — first stop after "Enter the Pit".
   Two-panel page: camera preference (left) + context card (right).
   Only the practice type is required; everything else is optional.
   The context is persisted to localStorage so navigation / refreshes never
   lose it, and every new visit replaces the previous context so a partner
   never sees a stale scenario. It travels: Setup → PreCall → Matchmaking
   (heartbeat) → Briefing → VideoCall. */

export const LS_CONTEXT = "salespit.practice_context";
export const LS_CAMERA = "salespit.camera_on";
export const SS_MATCH = "salespit.matched_payload";

export const PRACTICE_TYPES = [
  { key: "full_call", title: "Full Call", desc: "Practise a complete conversation from introduction to close." },
  { key: "outbound", title: "Outbound / Cold Call", desc: "Practise reaching out to prospects who haven't expressed interest yet." },
  { key: "inbound", title: "Inbound / Warm Call", desc: "Practise speaking with leads who've enquired, opted in, or shown interest." },
  { key: "objection_handling", title: "Objection Handling", desc: "Practise responding to concerns and overcoming objections." },
  { key: "pitch", title: "Pitch", desc: "Practise presenting your offer and communicating its value." },
  { key: "negotiation_closing", title: "Negotiation / Closing", desc: "Practise negotiating terms and securing the deal." },
  { key: "other", title: "Other", desc: "Set up a custom scenario to practise something specific." },
];

export const PROSPECT_TYPES = [
  { key: "interested_sceptical", label: "Interested but sceptical" },
  { key: "cold_resistant", label: "Cold and resistant" },
  { key: "price_sensitive", label: "Price-sensitive" },
  { key: "busy_distracted", label: "Busy and distracted" },
  { key: "ready_to_buy", label: "Ready to buy" },
  { key: "partner_decides", label: "Let my partner decide" },
];

export const DIFFICULTIES = [
  { key: "beginner", label: "Beginner" },
  { key: "intermediate", label: "Intermediate" },
  { key: "advanced", label: "Advanced" },
];

const loadStoredContext = () => {
  try {
    const raw = localStorage.getItem(LS_CONTEXT);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const cardCls = (sel) =>
  `rounded-2xl border p-4 text-left transition-[border-color,background-color] duration-200 ${
    sel
      ? "border-[#E52525] bg-[#E52525]/10 shadow-[0_0_24px_rgba(229,37,37,0.15)]"
      : "border-white/10 bg-[#141414] hover:border-white/25"
  }`;

const chipCls = (sel) =>
  `rounded-full border px-4 py-2 text-sm font-medium transition-[border-color,background-color,color] duration-200 ${
    sel
      ? "border-[#E52525] bg-[#E52525]/15 text-white"
      : "border-white/10 bg-[#141414] text-zinc-400 hover:border-white/25 hover:text-zinc-200"
  }`;

export default function PracticeSetup() {
  const navigate = useNavigate();
  const stored = loadStoredContext();

  const [cameraOn, setCameraOn] = useState(
    localStorage.getItem(LS_CAMERA) !== "0"
  );
  const [practiceType, setPracticeType] = useState(stored?.practice_type || "");
  const [selling, setSelling] = useState(stored?.selling || "");
  const [extra, setExtra] = useState(stored?.additional_context || "");
  const [prospect, setProspect] = useState(stored?.prospect_type || "partner_decides");
  const [difficulty, setDifficulty] = useState(stored?.difficulty || "beginner");
  const [showTypeError, setShowTypeError] = useState(false);

  const pickType = (key) => {
    setPracticeType(key);
    setShowTypeError(false);
  };

  const handleContinue = () => {
    if (!practiceType) {
      setShowTypeError(true);
      toast.error("Pick what you want to practise first.");
      return;
    }
    const context = {
      practice_type: practiceType,
      selling: selling.trim(),
      additional_context: extra.trim(),
      prospect_type: prospect,
      difficulty,
    };
    // Persist so refresh / navigation never loses it; replacing the stored
    // value guarantees a new partner never sees a stale scenario.
    localStorage.setItem(LS_CONTEXT, JSON.stringify(context));
    localStorage.setItem(LS_CAMERA, cameraOn ? "1" : "0");
    sessionStorage.removeItem(SS_MATCH); // stale match payload from any previous run
    navigate("/pre-call", { state: { cameraOn, context } });
  };

  return (
    <main
      className="min-h-screen bg-[#0A0A0A] px-5 pb-16 font-body text-white sm:px-8"
      data-testid="practice-setup-page"
    >
      {/* header */}
      <header className="mx-auto flex max-w-6xl items-center py-6">
        <button
          type="button"
          data-testid="practice-setup-back-btn"
          onClick={() => navigate("/homepage")}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </header>

      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="grid gap-8 lg:grid-cols-2"
        >
          {/* LEFT — choose your call mode */}
          <section>
            <h2 className="font-heading text-lg font-bold tracking-tight text-white">
              Choose your call mode
            </h2>
            <p className="mt-1.5 text-sm font-light text-zinc-400">
              Practise face-to-face over video, or simulate a real phone call.
            </p>
            <div className="mt-5 space-y-3">
              <button
                type="button"
                data-testid="camera-on-option"
                onClick={() => setCameraOn(true)}
                className={`flex w-full items-start gap-4 ${cardCls(cameraOn)}`}
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                    cameraOn ? "bg-[#E52525] text-white" : "bg-white/5 text-zinc-400"
                  }`}
                >
                  <Video size={20} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">Camera On</span>
                  <span className="mt-0.5 block text-xs font-medium text-zinc-300">
                    Face-to-face mock calls
                  </span>
                  <span className="mt-1 block text-xs font-light leading-relaxed text-zinc-400">
                    Practise sales conversations face-to-face with another rep,
                    just like a real video sales call.
                  </span>
                </span>
              </button>
              <button
                type="button"
                data-testid="camera-off-option"
                onClick={() => setCameraOn(false)}
                className={`flex w-full items-start gap-4 ${cardCls(!cameraOn)}`}
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                    !cameraOn ? "bg-[#E52525] text-white" : "bg-white/5 text-zinc-400"
                  }`}
                >
                  <Phone size={20} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">Camera Off</span>
                  <span className="mt-0.5 block text-xs font-medium text-zinc-300">
                    Realistic phone call simulation
                  </span>
                  <span className="mt-1 block text-xs font-light leading-relaxed text-zinc-400">
                    Simulate real inbound and outbound calls with realistic
                    ringing, helping you practise cold calling and build
                    confidence on the phone.
                  </span>
                </span>
              </button>
            </div>
          </section>

          {/* RIGHT — context card */}
          <section className="rounded-3xl border border-white/10 bg-[#111111] p-6 sm:p-8">
            <h1 className="font-heading text-2xl font-black tracking-tight text-white sm:text-3xl">
              Context Card
            </h1>
            <p className="mt-2 text-sm font-light text-zinc-400">
              Give your partner some context so you can get straight into practice.
            </p>

            {/* A. practice type */}
            <div className="mt-7">
              <h3 className="text-sm font-semibold text-white">
                What do you want to practise? <span className="text-[#E52525]">*</span>
              </h3>
              {showTypeError && !practiceType && (
                <p
                  data-testid="practice-type-error"
                  className="mt-2 text-xs font-medium text-[#FF5A5A]"
                >
                  Choose one option to continue.
                </p>
              )}
              <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                {PRACTICE_TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`practice-type-${t.key}`}
                    onClick={() => pickType(t.key)}
                    className={cardCls(practiceType === t.key)}
                  >
                    <span className="block text-sm font-semibold text-white">{t.title}</span>
                    <span className="mt-1 block text-xs font-light leading-relaxed text-zinc-400">
                      {t.desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* B. selling */}
            <div className="mt-7">
              <label
                htmlFor="selling-input"
                className="text-sm font-semibold text-white"
              >
                What are you selling?{" "}
                <span className="font-light text-zinc-500">(optional)</span>
              </label>
              <input
                id="selling-input"
                data-testid="selling-input"
                type="text"
                value={selling}
                maxLength={120}
                onChange={(e) => setSelling(e.target.value)}
                placeholder="e.g. software, insurance, coaching, property…"
                className="mt-2.5 w-full rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-[#E52525]/60"
              />
            </div>

            {/* C. additional context */}
            <div className="mt-6">
              <label
                htmlFor="additional-context-input"
                className="text-sm font-semibold text-white"
              >
                Additional context{" "}
                <span className="font-light text-zinc-500">(optional)</span>
              </label>
              <textarea
                id="additional-context-input"
                data-testid="additional-context-input"
                value={extra}
                maxLength={1000}
                rows={3}
                onChange={(e) => setExtra(e.target.value)}
                placeholder="Describe your offer, the situation, or what you want to work on…"
                className="mt-2.5 w-full resize-none rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-[#E52525]/60"
              />
            </div>

            {/* D. prospect type */}
            <div className="mt-7">
              <h3 className="text-sm font-semibold text-white">
                What type of prospect should your partner play?
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {PROSPECT_TYPES.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    data-testid={`prospect-${p.key}`}
                    onClick={() => setProspect(p.key)}
                    className={chipCls(prospect === p.key)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* E. difficulty */}
            <div className="mt-7">
              <h3 className="text-sm font-semibold text-white">Difficulty</h3>
              <p className="mt-1 text-xs font-light text-zinc-500">
                How challenging should the prospect be to handle?
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {DIFFICULTIES.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    data-testid={`difficulty-${d.key}`}
                    onClick={() => setDifficulty(d.key)}
                    className={chipCls(difficulty === d.key)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.45 }}
          className="mt-10 flex justify-center"
        >
          <button
            type="button"
            data-testid="practice-continue-btn"
            onClick={handleContinue}
            className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#E52525] px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.35)] transition-[background-color,box-shadow] hover:bg-[#FF3333] hover:shadow-[0_0_45px_rgba(229,37,37,0.5)]"
          >
            Continue to Device Check
            <ChevronRight size={18} className="transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>
      </div>
    </main>
  );
}
